import { Show, useRecordContext, useRedirect, RaRecord } from 'react-admin';
import {
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  Box,
} from '@mui/material';
import { SectionCard, Row, fmtCalendarDate, fmtDay } from '../../shared/Panel';
import { NameCell, SourceCell, CooperationCell } from './cells';
import { CarrierContacts, CarrierDetails, CarrierTmsContacts, HistoryProfile } from './CarrierSections';
import { CarrierVerificationSection } from './CarrierVerification';
import { FeatureGate } from '../featureFlags/FeatureGate';

type LooseRecord = RaRecord & Record<string, unknown>;

const orDash = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);

const present = (v: unknown): v is string | number =>
  (typeof v === 'string' && v.trim() !== '') || typeof v === 'number';

/**
 * Adres i licencja — tylko wypełnione wiersze (07.10.2026). TMS ich nie
 * podaje, więc „Adres”, „Numer licencji” i „Licencja ważna do” stały
 * zawsze puste; brak mówi zdanie pod tabelą.
 */
export const CarrierFacts = ({ record }: { record: LooseRecord }) => {
  const address = [record.address, record.postalCode, record.city, record.countryCode]
    .filter(present)
    .join(', ');
  const rows = [
    address ? { label: 'Adres', value: address } : null,
    present(record.licenseNumber) ? { label: 'Numer licencji', value: String(record.licenseNumber) } : null,
    present(record.licenseExpiryDate)
      ? { label: 'Licencja ważna do', value: fmtCalendarDate(String(record.licenseExpiryDate)) }
      : null,
  ].filter((r): r is { label: string; value: string } => r !== null);
  if (rows.length === 0) return null;
  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableBody>
        {rows.map((r) => (
          <Row key={r.label} label={r.label}>
            {r.value}
          </Row>
        ))}
      </TableBody>
    </Table>
  );
};

/** Co daje TMS, a co uzupełnia Trade — pod nagłówkiem karty. */
export const CARRIER_SOURCE_NOTE =
  'Z TMS dostajemy numer i nazwę przewoźnika oraz jego kontakty z kartoteki (sekcja „Kontakty z TMS”) — adresu ani licencji TMS nie podaje. NIP/VAT, współpracę i własne kontakty uzupełnia Trade poniżej, a profil powstaje z historii zleceń.';

const Header = () => {
  const r = useRecordContext<LooseRecord>();
  if (!r) return null;
  return (
    <SectionCard title="Przewoźnik">
      <NameCell record={r} />
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mt: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <SourceCell record={r} />
        <CooperationCell record={r} />
      </Box>
      <CarrierFacts record={r} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: 'block' }}
      >
        {CARRIER_SOURCE_NOTE}
      </Typography>
    </SectionCard>
  );
};

/**
 * Zlecenia wykonane przez tego przewoźnika.
 *
 * Numer pojazdu jest tu istotny: przy zleceniach podwykonawczych to jedyny
 * ślad, czym pojechało — takiego auta nie ma w naszej flocie.
 */
const RecentOrders = () => {
  const r = useRecordContext<LooseRecord>();
  const redirect = useRedirect();
  const orders = (r?.recentOrders as LooseRecord[] | undefined) ?? [];
  const total =
    (r?._count as { userOffers?: number } | undefined)?.userOffers ?? 0;
  if (!r) return null;
  return (
    <SectionCard title={`Ostatnie zlecenia (${orders.length} z ${total})`}>
      {orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak zleceń powiązanych z tym przewoźnikiem.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Numer</TableCell>
              <TableCell>Załadunek</TableCell>
              <TableCell>Relacja</TableCell>
              <TableCell>Pojazd</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((z) => (
              <TableRow
                key={String(z.id)}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => redirect('show', 'user-offers', z.id as number)}
              >
                <TableCell>{orDash(z.externalId)}</TableCell>
                <TableCell>{fmtDay(z.fromDate as string)}</TableCell>
                <TableCell>
                  {[z.fromCity, z.toCity].filter(Boolean).join(' → ') || '—'}
                </TableCell>
                <TableCell>{orDash(z.assignedVehicleNumber)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
};

export const CarrierShow = () => (
  <Show title="Przewoźnik">
    <Stack spacing={2} sx={{ p: 2 }}>
      <Header />
      <CarrierDetails />
      {/* Weryfikacja przewoźnika (S-24.10-11) — tylko z przełącznikiem `carrierVerification`. */}
      <CarrierVerificationSection />
      <CarrierContacts />
      {/* Kontakty z TMS (16.10.2026) — od wydania 26.10.2026 za przełącznikiem (S-26.10-03). */}
      <FeatureGate feature="tmsContactsAndCustomerTypes">
        <Box data-testid="carrier-tms-contacts">
          <CarrierTmsContacts />
        </Box>
      </FeatureGate>
      <HistoryProfile />
      <RecentOrders />
    </Stack>
  </Show>
);
